import React, { useEffect, useState } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate, Outlet } from "react-router-dom";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import AddJob from "@/pages/AddJob";
import JobDetail from "@/pages/JobDetail";
import Permits from "@/pages/Permits";
import Inspections from "@/pages/Inspections";
import AlertsSettings from "@/pages/AlertsSettings";
import AlertHistory from "@/pages/AlertHistory";
import Account from "@/pages/Account";
import BillingSuccess from "@/pages/BillingSuccess";
import Feedback from "@/pages/Feedback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundPolicy from "@/pages/RefundPolicy";
import AdminPanel from "@/pages/AdminPanel";
import CalendarView from "@/pages/CalendarView";
import { Layout } from "@/components/Layout";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LanguageProvider } from "@/i18n/LanguageContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function ProtectedShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user ? { user: location.state.user } : null);
  const [checking, setChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (auth) return;
    api.get("/auth/me")
      .then(r => setAuth({ user: r.data }))
      .catch(() => navigate("/login"))
      .finally(() => setChecking(false));
  }, [auth, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-950 rounded-full animate-spin" />
      </div>
    );
  }
  if (!auth) return null;
  return <Layout user={auth.user} outletUser={auth.user} />;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously detect OAuth callback in URL hash
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refunds" element={<RefundPolicy />} />
      <Route element={<ProtectedShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/jobs/new" element={<AddJob />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/permits" element={<Permits />} />
        <Route path="/inspections" element={<Inspections />} />
        <Route path="/calendar" element={<CalendarView />} />
        <Route path="/alerts/settings" element={<AlertsSettings />} />
        <Route path="/alerts/history" element={<AlertHistory />} />
        <Route path="/account" element={<Account />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppRouter />
        <LanguageSwitcher />
      </BrowserRouter>
    </LanguageProvider>
  );
}
